import type { StaffPreset } from "../domain/settings";
import type { ClinicId, Provider, ProviderId } from "../domain/types";

export interface StaffContext {
  providers: Provider[];
  clinicIds: ClinicId[];
  currentUserId: ProviderId;
  /** The clinic's default staff for the day (`/clinics/{id}/default-staff/`); null when none. */
  teamIds: ProviderId[] | null;
}

export interface StaffList {
  ids: ProviderId[];
  preset: StaffPreset;
}

/**
 * Whether "Just me" can mean anything (ENG-2209): the signed-in user is an active person on
 * this practice's staff list. A support login, or someone deactivated, is not — "Just me"
 * would draw an empty grid — so the control offers it disabled, with the reason.
 */
export const justMeAvailable = (
  providers: Provider[],
  currentUserId: ProviderId
): boolean => providers.some((p) => p.id === currentUserId && p.isActive);

/**
 * What the quick-set control shows for a stored preset. A stored "Just me" the user cannot
 * have right now reads as "Everyone" — what the grid actually draws — while the stored
 * preference itself is kept, so it comes back once they are on the staff list again.
 */
export const shownPreset = (
  preset: StaffPreset | null,
  justMe: boolean
): StaffPreset | null => (preset === "me" && !justMe ? "everyone" : preset);

/**
 * A saved "Just me" list the user can no longer have (ENG-2209): the preset is "me", Just me
 * is unavailable (they were deactivated, or are not on the staff list), and the list holds
 * nobody but them. The catalog keeps inactive staff, so such a list survives hydration and
 * would draw one inactive column while the control, through `shownPreset`, says Everyone.
 * The route re-runs the staff defaults for it, which draw everyone and keep the preset.
 */
export const staleJustMeList = (
  preset: StaffPreset | null,
  staffListIds: ProviderId[],
  providers: Provider[],
  currentUserId: ProviderId
): boolean =>
  preset === "me" &&
  staffListIds.length > 0 &&
  staffListIds.every((id) => id === currentUserId) &&
  !justMeAvailable(providers, currentUserId);

/**
 * The quick sets above the staff list. "Clinic team" is the clinic's default staff and
 * falls back to the signed-in user when the clinic has none, the old scheduler's
 * first-visit order. "Everyone" is every active provider at the selected clinics.
 */
export function staffForPreset(
  preset: StaffPreset,
  ctx: StaffContext
): StaffList {
  const active = new Set(
    ctx.providers.filter((p) => p.isActive).map((p) => p.id)
  );
  if (preset === "everyone") {
    return {
      preset,
      ids: ctx.providers
        .filter(
          (p) =>
            p.isActive &&
            p.isProvider &&
            p.clinicIds.some((c) => ctx.clinicIds.includes(c))
        )
        .map((p) => p.id)
    };
  }
  if (preset === "team") {
    const team = (ctx.teamIds ?? []).filter((id) => active.has(id));
    if (team.length > 0) return { preset, ids: team };
  }
  // A signed-in user who is not on the staff list (a support login) would get an empty
  // grid from "Just me"; show everyone instead. The control says so (`justMeAvailable`).
  if (!justMeAvailable(ctx.providers, ctx.currentUserId))
    return staffForPreset("everyone", ctx);
  return { preset: "me", ids: [ctx.currentUserId] };
}
